import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import { getPerformanceStatus } from '../lib/heatmapUtils';

/**
 * Trend chart showing FPS over CLs for a specific device
 */
function TrendChart({ data, onCLClick }) {
  if (!data || data.length === 0) {
    return <div className="trend-chart-empty">No trend data available</div>;
  }

  // Reverse data so oldest is first (left side of chart)
  const chartData = [...data].reverse();

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    const point = payload[0]?.payload;
    return (
      <div className="chart-tooltip">
        <strong>CL {label}</strong>
        {point?.timestamp && (
          <div className="tooltip-date">
            {new Date(point.timestamp).toLocaleDateString()}
          </div>
        )}
        {payload.map(p => (
          <div key={p.name} style={{ color: p.color }}>
            {p.name}: {p.value} FPS
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="trend-chart">
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
          <XAxis
            dataKey="cl"
            tick={{ fontSize: 10 }}
            angle={-45}
            textAnchor="end"
            height={50}
          />
          <YAxis domain={[0, 'auto']} tick={{ fontSize: 11 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <ReferenceLine y={45} stroke="#10b981" strokeDasharray="5 5" label={{ value: '45', position: 'right', fontSize: 10 }} />
          <ReferenceLine y={30} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: '30', position: 'right', fontSize: 10 }} />
          <Line
            type="monotone"
            dataKey="avgFps"
            name="AVG"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5, cursor: 'pointer', onClick: (e, payload) => onCLClick?.(payload.payload.cl) }}
          />
          <Line
            type="monotone"
            dataKey="lowFps"
            name="LOW (P5)"
            stroke="#ef4444"
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5, cursor: 'pointer', onClick: (e, payload) => onCLClick?.(payload.payload.cl) }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Bottleneck bar showing GPU vs CPU breakdown
 */
function BottleneckBar({ gpuPct, cpuPct }) {
  return (
    <div className="bottleneck-section">
      <div className="bottleneck-header">
        <span className="bottleneck-title">Bottleneck (samples &lt; 45 FPS)</span>
      </div>
      <div className="bottleneck-bar">
        <div className="bottleneck-item">
          <div className="bottleneck-label">
            <span className="bottleneck-type">GPU</span>
            <span className="bottleneck-pct">{gpuPct || 0}%</span>
          </div>
          <div className="bottleneck-bar-track">
            <div
              className="bottleneck-bar-fill gpu"
              style={{ width: `${gpuPct || 0}%` }}
            />
          </div>
          <span className="bottleneck-hint">&rarr; Art: textures, overdraw</span>
        </div>
        <div className="bottleneck-item">
          <div className="bottleneck-label">
            <span className="bottleneck-type">CPU</span>
            <span className="bottleneck-pct">{cpuPct || 0}%</span>
          </div>
          <div className="bottleneck-bar-track">
            <div
              className="bottleneck-bar-fill cpu"
              style={{ width: `${cpuPct || 0}%` }}
            />
          </div>
          <span className="bottleneck-hint">&rarr; Eng: scripts, loading</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Device list showing performance per device/quality combo
 */
function DeviceList({ devices, expandedDevice, onDeviceClick }) {
  if (!devices || devices.length === 0) {
    return <div className="device-list-empty">No device data</div>;
  }

  return (
    <div className="device-list">
      <div className="device-list-header">
        <span>DEVICES</span>
      </div>
      {devices.map((device, idx) => {
        const status = getPerformanceStatus(device.avgFps, device.lowFps);
        const isExpanded = expandedDevice?.device === device.device && expandedDevice?.quality === device.quality;
        return (
          <div
            key={`${device.device}-${device.quality}-${idx}`}
            className={`device-row ${isExpanded ? 'expanded' : ''}`}
            onClick={() => onDeviceClick(device.device, device.quality)}
          >
            <span className="device-expand-icon">{isExpanded ? '\u25BC' : '\u25B6'}</span>
            <span className={`device-status ${status.className}`}>{status.emoji}</span>
            <span className="device-name">{device.device}</span>
            <span className="device-quality">({device.quality})</span>
            <span className="device-fps">
              {device.avgFps} AVG, {device.lowFps} LOW
            </span>
            {device.bottleneck && device.avgFps < 45 && (
              <span className={`device-bottleneck ${device.bottleneck.toLowerCase()}`}>
                {device.bottleneck}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Performance analysis section shown in expanded area view
 */
function PerformanceAnalysis({ area, api, daysFilter, startDate = null, endDate = null, branch, filters = {}, onViewInHeatmap }) {
  const [perfData, setPerfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDevice, setExpandedDevice] = useState(null);
  const [deviceTrend, setDeviceTrend] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  // Stable serialization to avoid re-fetching on every render
  const filtersKey = JSON.stringify(filters);

  // Fetch area performance data
  useEffect(() => {
    const currentFilters = JSON.parse(filtersKey);
    async function fetchPerformance() {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getAreaPerformance(area.name, daysFilter, branch, currentFilters, startDate, endDate);
        setPerfData(result.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchPerformance();
  }, [api, area.name, daysFilter, startDate, endDate, branch, filtersKey]);

  // Fetch device trend when a device is expanded
  useEffect(() => {
    if (!expandedDevice) {
      setDeviceTrend(null);
      return;
    }

    async function fetchTrend() {
      setTrendLoading(true);
      try {
        const result = await api.getAreaDeviceTrend(
          area.name,
          expandedDevice.device,
          expandedDevice.quality,
          daysFilter,
          branch,
          startDate,
          endDate
        );
        setDeviceTrend(result.data);
      } catch (err) {
        console.error('Failed to fetch device trend:', err);
        setDeviceTrend(null);
      } finally {
        setTrendLoading(false);
      }
    }
    fetchTrend();
  }, [api, area.name, expandedDevice, daysFilter, startDate, endDate, branch]);

  const handleDeviceClick = useCallback((device, quality) => {
    setExpandedDevice(prev => {
      if (prev?.device === device && prev?.quality === quality) {
        return null; // Collapse if already expanded
      }
      return { device, quality };
    });
  }, []);

  const handleCLClick = useCallback((cl) => {
    if (onViewInHeatmap) {
      onViewInHeatmap(cl);
    }
  }, [onViewInHeatmap]);

  if (loading) {
    return (
      <div className="performance-analysis loading">
        <div className="loading-spinner"></div>
        <span>Loading performance data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="performance-analysis error">
        <span>Failed to load performance data: {error}</span>
      </div>
    );
  }

  if (!perfData) {
    return (
      <div className="performance-analysis empty">
        <span>No performance data available for this area.</span>
      </div>
    );
  }

  return (
    <div className="performance-analysis">
      <h4>Performance Analysis</h4>

      <BottleneckBar gpuPct={perfData.gpuPct} cpuPct={perfData.cpuPct} />

      <DeviceList
        devices={perfData.devices}
        expandedDevice={expandedDevice}
        onDeviceClick={handleDeviceClick}
      />

      {expandedDevice && (
        <div className="device-trend-section">
          {trendLoading ? (
            <div className="trend-loading">
              <div className="loading-spinner small"></div>
              <span>Loading trend data...</span>
            </div>
          ) : deviceTrend && deviceTrend.length > 0 ? (
            <>
              <div className="trend-header">
                <span className="trend-title">
                  {expandedDevice.device} ({expandedDevice.quality}) - FPS over CLs
                </span>
              </div>
              <TrendChart data={deviceTrend} onCLClick={handleCLClick} />
              <div className="trend-cl-list">
                {deviceTrend.slice(0, 5).map((point, idx) => {
                  const status = getPerformanceStatus(point.avgFps, point.lowFps);
                  const date = point.timestamp ? new Date(point.timestamp).toLocaleDateString() : '';
                  return (
                    <div key={point.cl} className="trend-cl-item">
                      <span className={`trend-cl-status ${status.className}`}>{status.emoji}</span>
                      <span className="trend-cl-number">CL {point.cl}</span>
                      <span className="trend-cl-date">{date}</span>
                      <span className="trend-cl-fps">{point.avgFps} AVG, {point.lowFps} LOW</span>
                      <button
                        className="trend-cl-view-btn"
                        onClick={() => handleCLClick(point.cl)}
                      >
                        View in Heatmap
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="trend-empty">No trend data for this device</div>
          )}
        </div>
      )}
    </div>
  );
}

export default PerformanceAnalysis;
